import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useHistory, Link, useParams } from 'react-router-dom';

import GeoLocatedFormIcon from 'apps/web_forms/assets/dispatched-form-geolocated.svg';
import GeoFencedFormIcon from 'apps/web_forms/assets/dispatched-form-geofenced.svg';
import MenuIcon from 'apps/web_forms/assets/hamburger-menu-icon.svg';
import FormList from 'apps/web_forms/components/FormList';
import FormListElement from 'apps/web_forms/components/FormListElement';
import FormPageHeader from 'apps/web_forms/components/FormPageHeader';
import GoogleMap from 'apps/web_forms/components/Map';
import DispatchMapMarker from 'apps/web_forms/components/commons/DispatchMapMarker';
import PlaceHolder from 'apps/web_forms/components/commons/PlaceHolder';
import DMButton from 'apps/web_forms/components/commons/DMButton';
import useLocation from 'apps/web_forms/hooks/useLocation';
import { useDispatches } from 'apps/web_forms/hooks/useDispatches';
import {
  APP_ROUTES,
  dispatchTypes,
  COLORS,
  ids,
  ERROR_LOADING_DISPATCHES,
} from 'apps/web_forms/utils/constants';
import { getDirectionUrl } from 'apps/web_forms/utils/googleMapsURLScheme';
import { getMeasureUnitFromString } from 'apps/web_forms/utils/inputsUtils';
import { locationParse, buildDistanceObject } from 'apps/web_forms/utils/locationHelpers';

const StyledContainer = styled.div`
  display: flex;

  & > div {
    width: 50%;
  }

  .form-elements-container {
    width: 100%;

    .form-list-element {
      width: 100%;
      margin: 0.5em 0;
    }
  }

  .map-container {
    height: 500px;
    margin-top: 0.5em;
    top: 2.5em;
    position: sticky;
  }
`;

const dispatchTypesMap = new Map([
  [
    dispatchTypes.GEO_LOCATED.toLowerCase(),
    {
      title: 'Geo-Located Dispatches',
      message:
        'Geo-located dispatches are sent specifically to you with a location attached.',
      icon: GeoLocatedFormIcon,
      iconHeight: undefined,
      initiallyOutOfRange: false,
    },
  ],
  [
    dispatchTypes.GEO_FENCED.toLowerCase(),
    {
      title: 'Geo-Fenced Dispatches',
      message:
        'Geo-fenced dispatches are sent specifically to you with a location and must be completed within the determined radius.',
      icon: GeoFencedFormIcon,
      iconHeight: '1.5em',
      initiallyOutOfRange: true,
    },
  ],
]);

const DispatchesDetails = () => {
  const [forms, setForms] = useState([]);
  const history = useHistory();
  const { setUserAccuracy, center, locationError } = useLocation();
  const { type } = useParams();
  const {
    isLoading,
    isError,
    error,
    dispatches,
    geolocatedDispatches,
    geofencedDispatches,
  } = useDispatches();

  const dispatchConfiguration = dispatchTypesMap.get(type);

  useEffect(() => {
    setUserAccuracy();
  }, []);

  useEffect(() => {
    if (!isLoading && !isError) {
      const { GEO_LOCATED, GEO_FENCED } = dispatchTypes;

      const filterBy =
        type === GEO_LOCATED.toLowerCase() ? GEO_LOCATED : GEO_FENCED;

      const filterList =
        filterBy === GEO_LOCATED ? geolocatedDispatches : geofencedDispatches;

      setForms(
        filterList.map((item) => {
          return { ...item, active: false };
        })
      );
    }
    /** Depend on dispatches, because it determines geolocatedDispatches and geofencedDispatches,
     * and they change too quickly by referenctial equality and depending on them causes an
     * infinite render loop
     **/
  }, [isLoading, isError, dispatches]);

  if (isError) {
    // TODO: Integrate Bugsnag to log the errors in prod.
    // eslint-disable-next-line no-console
    console.error(`${ERROR_LOADING_DISPATCHES}:`, error);
  }

  const DispatchesError = () => {
    return (
      <>
        <FormPageHeader
          title={'Dispatches'}
          description={dispatchConfiguration.message}
          small={false}
        />
        <PlaceHolder error>{ERROR_LOADING_DISPATCHES}</PlaceHolder>
      </>
    );
  };

  const goToForm = (id, version) => {
    history.push(APP_ROUTES.getFormPageById(id, `?origin=${type}`), {
      version,
    });
  };

  const onClick = (identifier) => {
    let newArray = [...forms];
    newArray = newArray.map((item) => ({ ...item, lastClick: false }));
    newArray[identifier] = {
      ...newArray[identifier],
      active: !newArray[identifier].active,
      lastClick: true,
    };

    setForms(newArray);
  };

  const render = () => {
    return (
      <>
        <FormPageHeader
          title={dispatchConfiguration.title}
          description={dispatchConfiguration.message}
          goBackUrl={APP_ROUTES.dispatches}
          renderToggleButton={false}
          renderRightButton={() => (
            <Link to={APP_ROUTES.dispatches}>
              <DMButton iconComponent={MenuIcon} filled>
                All dispatches list
              </DMButton>
            </Link>
          )}
        />
        <StyledContainer>
          <FormList
            title={null}
            description={null}
            loading={isLoading}
            forms={forms}
            searchBarWidth='100%'
          >
            {(form, index) => {
              const { latitude, longitude } = locationParse(form.location);

              const distance = buildDistanceObject(
                { latitude, longitude },
                center,
                locationError,
                getMeasureUnitFromString(form?.geoFenceRadius),
                dispatchConfiguration.initiallyOutOfRange
              );
              return (
                <FormListElement
                  key={form.identifier}
                  index={index}
                  identifier={form.identifier}
                  title={`${index + 1}. ${form.title}`}
                  description={form.description}
                  iconComponent={dispatchConfiguration.icon}
                  iconHeight={dispatchConfiguration.iconHeight}
                  iconColor={
                    distance.outOfRange
                      ? COLORS.dispatchDisabledColor
                      : COLORS.primary
                  }
                  iconLabel={form?.geoFenceRadius}
                  onClick={(formId) => goToForm(formId, form.version)}
                  buttons={[
                    {
                      id: ids.DispatchesDetailsIds.OpenButton,
                      label: 'Open',
                      onClick: (formId) => goToForm(formId, form.version),
                      highlighted: true,
                      disabled: distance.outOfRange,
                    },
                    {
                      id: ids.DispatchesDetailsIds.DirectionsButton,
                      label: 'Directions',
                      onClick: center
                        ? () => {
                            // eslint-disable-next-line no-undef
                            window.open(
                              getDirectionUrl(
                                { lat: center.lat, lng: center.lng },
                                { lat: latitude, lng: longitude }
                              ),
                              '_blank'
                            );
                          }
                        : () => {},
                    },
                  ]}
                  geoInfo={{
                    distance,
                    location: form.address,
                  }}
                  moreOption
                  openByDefault={form.active}
                  disabled={distance.outOfRange}
                  onMoreInfoClickHandler={onClick}
                />
              );
            }}
          </FormList>
          <div className='map-container'>
            <GoogleMap
              allowRemoveMap={false}
              markers={forms.map((form) => {
                const { latitude, longitude } = locationParse(form.location);
                return { ...form, latitude, longitude };
              })}
              canOverride
              openOnLoad
              centerOnMarkers
              onDragMethod={() => {}}
            >
              {(marker, index) => (
                <DispatchMapMarker
                  number={index + 1}
                  key={marker.identifier}
                  identifier={index}
                  lat={marker.latitude}
                  lng={marker.longitude}
                  clickCallback={onClick}
                  active={marker.active}
                  tooltip={marker.title}
                  lastClick={marker.lastClick}
                />
              )}
            </GoogleMap>
          </div>
        </StyledContainer>
      </>
    );
  };

  return isError ? <DispatchesError /> : render();
};

export default DispatchesDetails;
